import React, { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AGENT_STRIP, HERO_PREVIEW, type HeroPreviewCopy } from './heroPreviewCopy';

const HeroPreviewCopyContext = createContext<HeroPreviewCopy>(HERO_PREVIEW);

export function HeroPreviewCopyProvider({ children }: { children: React.ReactNode }) {
  const copy = useHeroPreviewCopyImpl();
  return <HeroPreviewCopyContext.Provider value={copy}>{children}</HeroPreviewCopyContext.Provider>;
}

/** i18n-backed hero workspace preview copy (falls back to English constants). */
export function useHeroPreviewCopy(): HeroPreviewCopy {
  return useContext(HeroPreviewCopyContext);
}

function useHeroPreviewCopyImpl(): HeroPreviewCopy {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const entries = (Object.keys(HERO_PREVIEW) as (keyof typeof HERO_PREVIEW)[]).map((key) => [
      key,
      t(`home.heroPreview.${key}`, { defaultValue: HERO_PREVIEW[key] }),
    ]);
    return Object.fromEntries(entries) as HeroPreviewCopy;
  }, [t, i18n.language]);
}

export function useAgentStripCopy() {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const queriesFromPreview = t('home.heroPreview.agentStripQueries', {
      returnObjects: true,
      defaultValue: [],
    }) as unknown;
    const queriesFromHero = t('home.hero.agentStrip.queries', {
      returnObjects: true,
      defaultValue: [],
    }) as unknown;
    const queries =
      (Array.isArray(queriesFromPreview) && queriesFromPreview.length > 0
        ? queriesFromPreview
        : Array.isArray(queriesFromHero) && queriesFromHero.length > 0
          ? queriesFromHero
          : AGENT_STRIP.queries) as string[];

    return {
      findJobs: t('home.heroPreview.agentStripFindJobs', {
        defaultValue: t('home.hero.agentStrip.findJobs', { defaultValue: AGENT_STRIP.findJobs }),
      }),
      orTypeYourOwn: t('home.heroPreview.agentStripOrTypeYourOwn', {
        defaultValue: t('home.hero.agentStrip.orTypeYourOwn', { defaultValue: AGENT_STRIP.orTypeYourOwn }),
      }),
      queries,
    };
  }, [t, i18n.language]);
}
